import { IAuthenticator } from "../authentication/authenticator";
import { IRootProps, IUser } from "@centraldispatch/singlespa-utilities";
import { IRouter } from "./router";
import { ISingleSpa } from "./single-spa";
import { logError, logInfo } from "../../logger";
import { User as OidcUser } from "oidc-client";
import { IGtm } from "../gtm/gtm";

export class Startup {
  private readonly _authenticator: IAuthenticator;
  private readonly _user: IUser;
  private readonly _router: IRouter;
  private _singleSpa: ISingleSpa;
  private readonly _gtm: IGtm;

  constructor(
    authenticator: IAuthenticator,
    user: IUser,
    router: IRouter,
    singleSpa: ISingleSpa,
    gtm: IGtm
  ) {
    this._authenticator = authenticator;
    this._user = user;
    this._router = router;
    this._singleSpa = singleSpa;
    this._gtm = gtm;
  }

  private _loadPage = () => {
    const props = {
      name: null,
      user: this._user,
      authentication: this._authenticator,
    } as IRootProps;

    this._singleSpa.start(props);
  };

  private _runProtectedPathStartup = (): void => {
    if (window.location.pathname.substring(1)) {
      //user is trying to access a protected path, and is not authed
      this._authenticator.loginAsync().catch(logError);
    } else {
      this._loadPage();
    }
  };

  private _startup = async (user: OidcUser): Promise<boolean> => {
    let userIsLoaded = false;

    if (user == null) {
      logInfo("No user was found");

      this._runProtectedPathStartup();
    } else if (user.expired) {
      logInfo("User found with expired token, attempting token renew");

      try {
        const user = await this._authenticator.loginSilentAsync();

        return await this._startup(user);
      } catch (e) {
        logInfo("Token could not be renewed, removing user from storage");

        await this._authenticator.getUserManager().removeUser();

        this._runProtectedPathStartup();
      }
    } else {
      const userProfile = await this._user.getProfileAsync();
      this._gtm.startGtm(userProfile);
      userIsLoaded = true;
      this._loadPage();
    }

    return userIsLoaded;
  };

  run = async (): Promise<boolean> => {
    if (this._router.canVisit()) {
      const user = await this._authenticator.getUserManager().getUser();

      const userIsLoaded = await this._startup(user);

      if (userIsLoaded) {
        const validateTokenClaims = async (redirectImmediately: boolean) => {
          if (process.env.VALIDATE_CUSTOMER_CLAIMS === "true") {
            console.info(
              "%c[Token Claims Validation] validating token claims",
              "color: green"
            );

            const profile = await this._user.getProfileAsync();

            const accountStatus = profile.accountStatus;

            console.info(
              `%c[Token Claims Validation] account status is ${accountStatus}`,
              "color: green"
            );

            if (accountStatus == "Inactive") {
              await this._authenticator.getUserManager().removeUser();
              await this._authenticator.getUserManager().revokeAccessToken();

              let counter = redirectImmediately ? 1 : 5;

              setInterval(() => {
                console.info(
                  `%c[Token Claims Validation] user is not active, redirecting in ${counter} seconds`,
                  "color: red"
                );

                counter--;

                if (!counter) {
                  this._router.redirectToLegacy();
                }
              }, 1000);
            }
          }
        };

        const userManager = this._authenticator.getUserManager();

        userManager.events.addUserLoaded(() => validateTokenClaims(false));

        userManager.events.addSilentRenewError(() => {
          userManager.removeUser();
          userManager.revokeAccessToken();
          this._authenticator.loginAsync();
        });

        await validateTokenClaims(true);
      }

      return userIsLoaded;
    } else {
      this._router.redirectToLegacy();
    }
  };
}
